import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Mail, RefreshCw, Trash2, Bell, Loader2, Receipt } from "lucide-react";

export default function Alerts() {
  const { t, i18n } = useTranslation("alerts");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [sendingReceipts, setSendingReceipts] = useState(false);
  const [lastSummary, setLastSummary] = useState(null);

  const KIND_CLS = {
    promoter_confirmed: "bg-emerald-100 text-emerald-800 border-emerald-200",
    event_filled: "bg-emerald-100 text-emerald-800 border-emerald-200",
    invoice_overdue: "bg-red-100 text-red-800 border-red-200",
    promoter_reminder: "bg-amber-100 text-amber-800 border-amber-200",
  };

  const refresh = () => {
    setLoading(true);
    api.get("/alerts/log").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const runChecks = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/alerts/run-checks");
      setLastSummary(data);
      refresh();
    } finally {
      setRunning(false);
    }
  };

  const clearLog = async () => {
    if (!window.confirm(t("clearConfirm"))) return;
    await api.delete("/alerts/log");
    refresh();
  };

  const sendReceiptReminders = async () => {
    if (!window.confirm("Enviar agora o lembrete de recibo por WhatsApp a todos os promotores com recibos pendentes deste mês?")) return;
    setSendingReceipts(true);
    try {
      const { data } = await api.post("/alerts/send-receipt-reminders");
      if (data.whatsapp_ready === false) {
        window.alert("O WhatsApp não está ligado. Liga o WhatsApp para enviar lembretes de recibo.");
      } else {
        window.alert(`Lembretes de recibo:\nCandidatos: ${data.candidates}\nEnviados: ${data.sent}\nFalhados: ${data.failed}\nIgnorados (sem telefone/já enviado): ${data.skipped}`);
      }
      refresh();
    } catch (err) {
      window.alert("Erro: " + (err?.response?.data?.detail || err.message));
    } finally {
      setSendingReceipts(false);
    }
  };

  const locale = i18n.language === "en" ? "en-GB" : "pt-PT";

  return (
    <div className="space-y-6" data-testid="alerts-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight text-zinc-950">{t("title")}</h1>
          <p className="text-sm text-zinc-500 mt-1">{t("subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={runChecks} disabled={running} data-testid="run-checks-btn" className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60">
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {t("runNow")}
          </button>
          <button onClick={sendReceiptReminders} disabled={sendingReceipts} data-testid="send-receipt-reminders-btn" className="inline-flex items-center gap-2 border border-emerald-300 text-emerald-800 bg-emerald-50 hover:bg-emerald-100 px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60">
            {sendingReceipts ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}
            Lembrete de recibo agora
          </button>
          <button onClick={clearLog} data-testid="clear-log-btn" className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-200 rounded-lg text-sm hover:bg-zinc-50">
            <Trash2 className="w-4 h-4" /> {t("clearLog")}
          </button>
        </div>
      </div>
      <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3" data-testid="receipt-reminder-auto-disabled-notice">
        <strong>Lembrete de recibos automático desativado.</strong> Já não é enviado no último dia útil do mês. Para lembrar os promotores com recibos pendentes, usa o botão "Lembrete de recibo agora".
      </div>

      {lastSummary && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-900" data-testid="last-summary">
          {t("summary", { overdue: lastSummary.overdue_alerts, sent: lastSummary.reminders_sent, failed: lastSummary.reminders_failed })}
        </div>
      )}

      <div className="bg-emerald-50/60 border border-emerald-200 rounded-xl p-4 flex items-start gap-3 text-sm text-emerald-900" data-testid="receipt-auto-note">
        <Receipt className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold">Lembrete de recibo automático</div>
          <div className="text-xs mt-0.5">É enviado automaticamente por WhatsApp no <strong>último dia útil de cada mês</strong> a todos os promotores com recibos pendentes (uma vez por mês). Podes também enviar já com o botão "Lembrete de recibo agora". Requer WhatsApp ligado.</div>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3 text-sm text-amber-900">
        <Mail className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold">{t("notice.title")}</div>
          <div className="text-xs mt-0.5">{t("notice.body")}</div>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl divide-y divide-zinc-100">
        {loading ? <div className="p-10 text-center text-sm text-zinc-500">…</div> : items.length === 0 ? (
          <div className="p-12 text-center">
            <Bell className="w-8 h-8 text-zinc-300 mx-auto" />
            <div className="text-sm text-zinc-500 mt-2">{t("empty")}</div>
          </div>
        ) : items.map((it) => {
          const label = t(`kinds.${it.kind}`, it.kind);
          const cls = KIND_CLS[it.kind] || "bg-zinc-100 text-zinc-700 border-zinc-200";
          return (
            <div key={it.id} data-testid={`alert-${it.id}`} className="px-5 py-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${cls}`}>{label}</span>
                    <span className="text-xs text-zinc-500 font-mono">{it.to}</span>
                  </div>
                  <div className="text-sm font-semibold text-zinc-900 mt-1.5">{it.subject}</div>
                  <div className="text-sm text-zinc-600 mt-1 whitespace-pre-wrap">{it.body}</div>
                </div>
                <div className="text-xs text-zinc-400 shrink-0">{new Date(it.sent_at).toLocaleString(locale)}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
